import { memo, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { JobPagination, JobSummary } from '@/services/jobs/model';
import type { SearchMethod } from '@/services/opportunity-tracking';
import { SOURCE_SCREENS } from '@/services/opportunity-tracking/source-screen';
import { JobCard } from './job-card';
import { JobResultsHeading } from './job-results-heading';
import type { JobResultsViewMode } from './job-results-view-toggle';
import { JobResultsSkeleton } from './job-results-skeleton';
import { JobsPagination } from './jobs-pagination';
import { JobDetailUnavailable } from './job-detail-unavailable';
import { JobInfiniteScrollTrigger } from './job-infinite-scroll-trigger';

type JobResultsProps = {
  errorMessage: string;
  infiniteScrollError?: string;
  hasMoreJobs?: boolean;
  isSavePending?: (jobId: string) => boolean;
  isSplitView: boolean;
  isSearching: boolean;
  isLoadingMoreJobs?: boolean;
  jobs: JobSummary[];
  onPageChange: (page: number, href: string) => void;
  onLoadMoreJobs?: () => void;
  onRetry: () => void;
  onSaveChange?: (job: JobSummary) => void;
  onScrollTargetHandled: () => void;
  onSelectJob: (job: JobSummary) => void;
  onViewModeChange: (viewMode: JobResultsViewMode) => void;
  pagination: JobPagination;
  requiresLogin?: boolean;
  savedJobIds: ReadonlySet<string>;
  searchLocations: string[];
  searchMethod?: SearchMethod;
  searchQuery: string;
  scrollTargetJobId: string | null;
  selectedJobId: string | null;
  viewMode: JobResultsViewMode;
};

export const JobResults = memo(function JobResults({
  errorMessage,
  infiniteScrollError,
  hasMoreJobs = false,
  isSavePending,
  isSplitView,
  isSearching,
  isLoadingMoreJobs = false,
  jobs,
  onPageChange,
  onLoadMoreJobs,
  onRetry,
  onSaveChange,
  onScrollTargetHandled,
  onSelectJob,
  onViewModeChange,
  pagination,
  requiresLogin = false,
  savedJobIds,
  searchLocations,
  searchMethod,
  searchQuery,
  scrollTargetJobId,
  selectedJobId,
  viewMode,
}: JobResultsProps) {
  const scrollTargetJobCardRef = useRef<HTMLDivElement>(null);
  const activeScrollTargetJobId = isSplitView
    ? selectedJobId
    : scrollTargetJobId;
  const displayedViewMode = isSplitView ? 'grid' : viewMode;

  useEffect(() => {
    if (!activeScrollTargetJobId) return;

    const animationFrame = requestAnimationFrame(() => {
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      scrollTargetJobCardRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: isSplitView ? 'start' : 'nearest',
        inline: 'nearest',
      });

      onScrollTargetHandled();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [activeScrollTargetJobId, isSplitView, onScrollTargetHandled]);

  if (errorMessage) {
    return <JobDetailUnavailable onRetry={onRetry} />;
  }

  if (isSearching) {
    return <JobResultsSkeleton isSplitView={isSplitView} viewMode={viewMode} />;
  }

  return (
    <section aria-label="Danh sách việc làm" className="min-w-0">
      <JobResultsHeading
        isSplitView={isSplitView}
        onViewModeChange={onViewModeChange}
        pagination={pagination}
        searchLocations={searchLocations}
        searchQuery={searchQuery}
        viewMode={viewMode}
      />

      <div
        className={cn(
          'grid grid-cols-1 pb-1',
          isSplitView
            ? 'gap-3'
            : displayedViewMode === 'list'
              ? 'gap-3 sm:gap-3'
              : 'gap-3 sm:gap-3 lg:grid-cols-2'
        )}
      >
        {jobs.map(job => {
          const isSelected = selectedJobId === job.id;
          const isScrollTarget = activeScrollTargetJobId === job.id;

          return (
            <div
              key={job.id}
              ref={isScrollTarget ? scrollTargetJobCardRef : undefined}
              className={cn(
                'h-full min-w-0 scroll-mt-20 [contain-intrinsic-size:auto_196px] [content-visibility:auto]',
                {
                  'mx-auto w-full max-w-[896px]': !isSplitView,
                }
              )}
            >
              <JobCard
                job={job}
                isSavePending={isSavePending?.(job.id)}
                isSaved={savedJobIds.has(job.id)}
                isSplitView={isSplitView}
                onSaveChange={onSaveChange}
                requiresLogin={requiresLogin}
                searchMethod={searchMethod}
                searchQuery={searchQuery}
                selected={isSelected}
                sourceScreen={SOURCE_SCREENS.SEARCH_RESULT}
                onSelect={onSelectJob}
                viewMode={displayedViewMode}
              />
            </div>
          );
        })}
      </div>

      {onLoadMoreJobs ? (
        <JobInfiniteScrollTrigger
          errorMessage={infiniteScrollError}
          hasMore={hasMoreJobs}
          isLoading={isLoadingMoreJobs}
          onLoadMore={onLoadMoreJobs}
        />
      ) : null}

      <JobsPagination
        pagination={pagination}
        disabled={isSearching}
        onPageChange={onPageChange}
      />
    </section>
  );
});
